import apiClient from './client'
import type {
  BotStatsTimeSeriesResponse,
  BotDailyMetric,
  ResourceStatsTimeSeriesResponse,
  ResourceMetric,
  Granularity,
  BotOwnerAggregatedStats,
  ResourceOwnerAggregatedStats,
} from '../types/statistics'

export const statisticsApi = {
  // Bot statistics
  getBotTimeSeries: async (
    botId: string,
    startDate: string,
    endDate: string,
    granularity: Granularity = 'day',
    refresh?: boolean
  ): Promise<BotStatsTimeSeriesResponse> => {
    console.log('[StatisticsAPI] Fetching bot time-series:', {
      botId,
      startDate,
      endDate,
      granularity,
      refresh,
    })

    try {
      const response = await apiClient.get(`/api/v1/bots/${botId}/statistics`, {
        params: {
          start_date: startDate,
          end_date: endDate,
          granularity,
          ...(refresh && { refresh: true }),
        },
      })
      console.log('[StatisticsAPI] Bot time-series response:', response.data)
      return response.data
    } catch (error) {
      console.error('[StatisticsAPI] Error fetching bot time-series:', error)
      throw error
    }
  },

  getBotLatestMetrics: async (
    botId: string,
    days: number = 7
  ): Promise<BotDailyMetric[]> => {
    console.log('[StatisticsAPI] Fetching bot latest metrics:', { botId, days })

    try {
      const response = await apiClient.get(`/api/v1/bots/${botId}/statistics/latest`, {
        params: { days },
      })
      console.log('[StatisticsAPI] Bot latest metrics response:', response.data)
      return response.data
    } catch (error) {
      console.error('[StatisticsAPI] Error fetching bot latest metrics:', error)
      throw error
    }
  },

  // Resource statistics
  getResourceTimeSeries: async (
    resourceId: string,
    startDate: string,
    endDate: string,
    granularity: Granularity = 'day',
    refresh?: boolean
  ): Promise<ResourceStatsTimeSeriesResponse> => {
    console.log('[StatisticsAPI] Fetching resource time-series:', {
      resourceId,
      startDate,
      endDate,
      granularity,
      refresh,
    })

    try {
      const response = await apiClient.get(`/api/v1/resources/${resourceId}/statistics`, {
        params: {
          start_date: startDate,
          end_date: endDate,
          granularity,
          ...(refresh && { refresh: true }),
        },
      })
      console.log('[StatisticsAPI] Resource time-series response:', response.data)
      return response.data
    } catch (error) {
      console.error('[StatisticsAPI] Error fetching resource time-series:', error)
      throw error
    }
  },

  getResourceLatestMetrics: async (
    resourceId: string,
    days: number = 7
  ): Promise<ResourceMetric[]> => {
    console.log('[StatisticsAPI] Fetching resource latest metrics:', { resourceId, days })

    try {
      const response = await apiClient.get(
        `/api/v1/resources/${resourceId}/statistics/latest`,
        {
          params: { days },
        }
      )
      console.log('[StatisticsAPI] Resource latest metrics response:', response.data)
      return response.data
    } catch (error) {
      console.error('[StatisticsAPI] Error fetching resource latest metrics:', error)
      throw error
    }
  },

  // User aggregated statistics - Bot owner
  getUserBotStats: async (
    userId: string,
    startDate: string,
    endDate: string,
    granularity: Granularity = 'day',
    refresh?: boolean
  ): Promise<BotOwnerAggregatedStats> => {
    console.log('[StatisticsAPI] Fetching user bot aggregated stats:', {
      userId,
      startDate,
      endDate,
      granularity,
      refresh,
    })

    try {
      const response = await apiClient.get(`/api/v1/users/${userId}/statistics/bots`, {
        params: {
          start_date: startDate,
          end_date: endDate,
          granularity,
          ...(refresh && { refresh: true }),
        },
      })
      console.log('[StatisticsAPI] User bot stats response:', response.data)
      return response.data
    } catch (error) {
      console.error('[StatisticsAPI] Error fetching user bot stats:', error)
      throw error
    }
  },

  // User aggregated statistics - Resource owner
  getUserResourceStats: async (
    userId: string,
    startDate: string,
    endDate: string,
    granularity: Granularity = 'day',
    refresh?: boolean
  ): Promise<ResourceOwnerAggregatedStats> => {
    console.log('[StatisticsAPI] Fetching user resource aggregated stats:', {
      userId,
      startDate,
      endDate,
      granularity,
      refresh,
    })

    try {
      const response = await apiClient.get(`/api/v1/users/${userId}/statistics/resources`, {
        params: {
          start_date: startDate,
          end_date: endDate,
          granularity,
          ...(refresh && { refresh: true }),
        },
      })
      console.log('[StatisticsAPI] User resource stats response:', response.data)
      return response.data
    } catch (error) {
      console.error('[StatisticsAPI] Error fetching user resource stats:', error)
      throw error
    }
  },
}
